import { useState } from "react";
import { BrandMark } from "@/components/BrandMark";
import ThemePicker from "@/components/dashboard/ThemePicker";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Pulse, MagnifyingGlass, ArrowLeft } from "@phosphor-icons/react";
import { GLOSSARY, GLOSSARY_CATEGORIES } from "@/lib/glossary";
import { useAuth } from "@/context/AuthContext";

export default function Glossary() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  const query = q.trim().toLowerCase();
  const filtered = GLOSSARY.filter((g) => {
    if (cat !== "All" && g.category !== cat) return false;
    if (!query) return true;
    return (g.term + " " + g.short + " " + g.long).toLowerCase().includes(query);
  });

  const cats = cat === "All" ? GLOSSARY_CATEGORIES : [cat];

  return (
    <div className="min-h-screen bg-bg text-ink" data-testid="glossary-page">
      <header className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="glossary-brand-link">
          <BrandMark size={32} />
          <span className="font-display text-xl font-semibold tracking-tight">Ikavio</span>
        </Link>
        <div className="flex items-center gap-3">
          <ThemePicker />
          <Link
            to={user ? "/app" : "/"}
            data-testid="glossary-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-muted2 hover:text-ink transition-colors"
          >
            <ArrowLeft size={15} weight="bold" /> {user ? "Back to dashboard" : "Back home"}
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 pb-20">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span className="label-eyebrow">Glossary</span>
          <h1 className="font-display font-light tracking-tighter text-4xl sm:text-5xl mt-2">
            Every term, in plain language
          </h1>
          <p className="text-muted2 mt-3 max-w-lg">
            Health shouldn't need a dictionary — but when it does, here it is. From Healthspan to HRV, everything Ikavio shows you, explained simply.
          </p>
        </motion.div>

        <div className="sticky top-0 z-20 bg-bg/90 backdrop-blur-md py-4 mt-6 -mx-2 px-2">
          <div className="flex items-center gap-2 bg-surface border border-border rounded-full px-4 py-1">
            <MagnifyingGlass size={17} color="#7C8AA5" />
            <input
              data-testid="glossary-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a term… e.g. HRV, biological age"
              className="flex-1 bg-transparent outline-none text-[15px] py-2.5"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {["All", ...GLOSSARY_CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`glossary-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`text-xs font-medium rounded-full px-3.5 py-1.5 transition-colors ${
                  cat === c ? "bg-ink text-bg" : "bg-secondary hover:bg-border"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted2" data-testid="glossary-empty">
            No terms match "{q}". Try a different word.
          </div>
        ) : (
          cats.map((c) => {
            const terms = filtered.filter((g) => g.category === c);
            if (!terms.length) return null;
            return (
              <section key={c} className="mt-10">
                <h2 className="label-eyebrow">{c}</h2>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  {terms.map((g, i) => (
                    <motion.div
                      key={g.id}
                      data-testid={`glossary-term-${g.id}`}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: (i % 4) * 0.05 }}
                      className="clay p-6"
                    >
                      <h3 className="font-display text-lg font-medium">{g.term}</h3>
                      <p className="text-sm text-brand mt-1.5 leading-relaxed">{g.short}</p>
                      <p className="text-sm text-muted2 mt-2 leading-relaxed">{g.long}</p>
                    </motion.div>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </main>

      <footer className="border-t border-border py-8 text-center text-sm text-muted2">
        Ikavio — Optimizing for healthy, capable years of life.
      </footer>
    </div>
  );
}
